import { derived, get, writable } from 'svelte/store';
import { api, ApiError } from './api.js';
import { applyAppSettingsToDocument, DEFAULT_APP_SETTINGS, normalizeAppSettings } from './app-settings.js';
import { applyAppBadge } from './app-badge.js';
import { compressImageDataUrl } from './image-compress.js';
// MQTT грузится ЛЕНИВО: тяжёлая (~372КБ) библиотека выносится в отдельный чанк, и
// app-бандл остаётся лёгким — iOS Safari парсит и монтирует приложение быстро (без
// минут белого экрана). MQTT подключается уже после первого рендера (startMqtt).
let mqttModulePromise = null;
function loadMqtt() {
  if (!mqttModulePromise) {
    mqttModulePromise = import('./mqtt.js');
  }
  return mqttModulePromise;
}
import {
  applyPartialToActivities,
  applyResponseToActivities,
  applyStatusToActivities,
  applyTechToActivities,
  isGeneratingState,
  normalizeChats,
  applyReadMarkToChats,
} from './chat-state.js';
import {
  clearDraft as clearDraftStorage,
  getDraft as getDraftStorage,
  getShowSystemMessages,
  setCachedAppSettings,
  setDraft as setDraftStorage,
  setShowSystemMessages as setShowSystemMessagesStorage,
} from './storage.js';
import { publishSyncEvent, subscribeSyncEvents } from './sync.js';
import { TEXT } from './ui-text.js';
import {
  acknowledge as acknowledgeDelivery,
  clear as clearDelivery,
  flushPending as flushPendingDelivery,
  setModelResolver as setDeliveryModelResolver,
} from './delivery.js';
import { clearSnapshot as clearMessageSnapshot } from './message-cache.js';

export const userStore = writable(null);
export const connectionStore = writable('disconnected');
export const connectionReasonStore = writable('startup');
export const mqttErrorStore = writable('');
export const chatsStore = writable([]);
// Число архивных чатов участника (строка «Архив · N» наверху ленты).
export const archivedCountStore = writable(0);
// Этап 25.1: состояние «мозга» (провайдеров LLM) — ok | degraded | down. Приходит
// со списком чатов и событием brain_status; строка состояния — BrainStatusLine.
export const BRAIN_LEVELS = ['ok', 'degraded', 'down'];
// Этап 25.4: exitsDown — сколько выходов пула лежит; этап 25.6: providerStates —
// состояние каждой модели ({ openai: 'ok' | 'down' | 'unknown', … }); этап 33:
// newModels — сколько новых моделей у провайдеров (чип «новые: N»). Приходят
// только владельцу сервера (остальным null) — ему видна строка моделей.
export const brainStatusStore = writable({ level: 'ok', since: null, exitsDown: 0, providerStates: null, newModels: 0 });

export function applyBrainStatus(value) {
  if (!value || typeof value !== 'object') {
    return;
  }
  const level = BRAIN_LEVELS.includes(value.level) ? value.level : 'ok';
  const exitsDown = Number.isInteger(value.exitsDown) && value.exitsDown > 0 ? value.exitsDown : 0;
  const states = value.providerStates && typeof value.providerStates === 'object' ? value.providerStates : null;
  const providerStates = states
    ? Object.fromEntries(Object.entries(states).filter(([, state]) => typeof state === 'string'))
    : null;
  const newModels = Number.isInteger(value.newModels) && value.newModels > 0 ? value.newModels : 0;
  brainStatusStore.set({ level, since: value.since ?? null, exitsDown, providerStates, newModels });
}
export const modelsStore = writable([]);
// Служебные модели (vision/search/image) из /api/models — для индикации в UI (не выбираются).
export const serviceModelsStore = writable(null);
// Дефолтная модель = помеченная isDefault в /api/models (единый источник — backend),
// иначе первая из каталога; null пока каталог не загружен.
export const defaultModelStore = derived(modelsStore, (list) =>
  list.find((m) => m.isDefault)?.id ?? list[0]?.id ?? null
);
export const currentChatId = writable(null);
export const showArchivedStore = writable(false);
export const loadingStore = writable(false);
// К4 (1.27.2): до первой удачной загрузки пустой chatsStore значит «ещё не знаем»,
// а не «чатов нет» — иначе сбой на старте выглядел как пропажа всех чатов.
export const chatsLoadedStore = writable(false);
export const chatsLoadErrorStore = writable('');
export const chatActivityStore = writable({});
export const flashStore = writable(null);
export const draftStore = writable({});
export const uiPrefsStore = writable({});
export const appSettingsStore = writable(DEFAULT_APP_SETTINGS);
// Семейный ростер: map userId -> {userId, displayName, avatarDataUrl}.
// Источник имён/аватарок авторов в общих чатах и списка для «Поделиться чатом».
export const usersStore = writable({});

let syncCleanup = null;
let flashTimer = null;
let audioContext = null;
let resumePromise = null;

export const statusStore = derived([chatActivityStore, currentChatId], ([$chatActivityStore, $currentChatId]) => {
  if (!$currentChatId) {
    return null;
  }
  return $chatActivityStore[$currentChatId] || null;
});

export const isGenerating = derived(statusStore, ($statusStore) => isGeneratingState($statusStore?.state));
export const canSend = derived([connectionStore, isGenerating], ([$connectionStore, $isGenerating]) => {
  return $connectionStore === 'connected' && !$isGenerating;
});

export function setConnectionState(state, { reason = 'runtime', message, clearMessage = false } = {}) {
  connectionStore.set(state);
  connectionReasonStore.set(reason);
  if (typeof message === 'string') {
    mqttErrorStore.set(message);
    return;
  }
  if (clearMessage) {
    mqttErrorStore.set('');
  }
}

export function setBrowserOfflineState(message = TEXT.connection.browserOffline) {
  setConnectionState('offline', {
    reason: 'browser-offline',
    message,
  });
}

export function clearBrowserOfflineState() {
  if (get(connectionStore) === 'offline') {
    setConnectionState('disconnected', {
      reason: 'network-recovered',
      clearMessage: true,
    });
  }
}

export function getConnectionBlockedMessage(state = get(connectionStore), message = get(mqttErrorStore)) {
  if (message) {
    return message;
  }

  if (state === 'offline') {
    return TEXT.connection.browserOffline;
  }
  if (state === 'connecting') {
    return TEXT.connection.connecting;
  }
  if (state === 'error') {
    return TEXT.connection.error;
  }
  if (state === 'disconnected') {
    return TEXT.connection.disconnected;
  }

  return '';
}

function ensureSyncSubscription() {
  if (syncCleanup) {
    return;
  }

  syncCleanup = subscribeSyncEvents(async (event) => {
    if (!event?.type) {
      return;
    }

    if (event.type === 'chats:refresh') {
      await refreshChatsInBackground();
      return;
    }

    if (event.type === 'chat:deleted') {
      const chatId = event.payload?.chatId;
      chatsStore.update((items) => items.filter((item) => item.chatId !== chatId));
      if (get(currentChatId) === chatId) {
        currentChatId.set(null);
        showFlash('Чат был удалён в другой вкладке.', 'warning');
        window.location.hash = '/chats';
      }
      return;
    }

    if (event.type === 'draft:changed' && event.payload?.chatId) {
      draftStore.update((current) => ({
        ...current,
        [event.payload.chatId]: getDraftStorage(event.payload.chatId),
      }));
      return;
    }

    if (event.type === 'pref:show-system' && event.payload?.chatId) {
      uiPrefsStore.update((current) => ({
        ...current,
        [event.payload.chatId]: {
          ...(current[event.payload.chatId] || {}),
          showSystemMessages: getShowSystemMessages(event.payload.chatId),
        },
      }));
    }
  });
}

// action — кнопка в тосте (1.27.3: «Отменить» после архивации свайпом):
// { label, run }. Нажатие выполняет run и гасит тост.
export function showFlash(message, level = 'info', timeoutMs = 4000, action = null) {
  flashStore.set({ message, level, action });
  if (flashTimer) {
    window.clearTimeout(flashTimer);
  }
  if (typeof window !== 'undefined') {
    flashTimer = window.setTimeout(() => {
      flashStore.set(null);
      flashTimer = null;
    }, timeoutMs);
  }
}

export function runFlashAction() {
  const current = get(flashStore);
  if (flashTimer) {
    window.clearTimeout(flashTimer);
    flashTimer = null;
  }
  flashStore.set(null);
  return current?.action?.run ? current.action.run() : undefined;
}

export function setMqttError(message) {
  setConnectionState(get(connectionStore), {
    reason: get(connectionReasonStore),
    message: message || '',
  });
}

export function clearMqttError() {
  setConnectionState(get(connectionStore), {
    reason: get(connectionReasonStore),
    clearMessage: true,
  });
}

function delay(ms) {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });
}

export async function initApp() {
  ensureSyncSubscription();

  // Офлайн-очередь доставки: сообщения, отправленные без модели (cold-start офлайн),
  // получают дефолтную модель в момент фактической отправки.
  setDeliveryModelResolver(() => get(appSettingsStore).defaultModel ?? get(defaultModelStore));

  // Этап 30: первый экран одним запросом. На старом backend — прежние запросы по одному.
  if (await startInOneRequest()) {
    void runAfterStart([() => loadUsers(), () => keepPushSubscriptionSafe({ resave: true })]);
    return;
  }

  const whoami = await api.whoami();
  userStore.set(whoami.userId);

  // MQTT в initApp НЕ подключаем — это тяжёлый ленивый чанк. Стартуем приложение без
  // него (быстрый монтаж), MQTT поднимется отдельно после первого рендера — startMqtt().
  // Последовательно и устойчиво: iOS WebKit прерывисто роняет ПАРАЛЛЕЛЬНЫЕ fetch к
  // /api (NETWORK), а Promise.all валит этим весь старт («Ошибка запуска»). Грузим по
  // одному; сбой любого из них не блокирует старт — данные подтянутся при resume/повторе.
  // Критичен только whoami (выполнен выше).
  for (const task of [() => loadAppSettings(), () => loadModels(), () => loadChats(false)]) {
    try {
      await task();
    } catch (error) {
      console.warn('initApp: non-critical load failed (continuing)', error);
    }
  }
  // К4 (1.27.2): люди и push-подписка первому экрану не нужны — грузятся после
  // старта. Push ждал serviceWorker.ready без срока, и «Загрузка…» могла не уйти.
  void runAfterStart([() => loadUsers(), () => keepPushSubscriptionSafe({ resave: true })]);
}

// Этап 30: /start = whoami + общие настройки + модели + список чатов одним ответом
// (iOS WebKit роняет параллельные fetch, и четыре запроса шли по очереди). Нет сессии
// (401) и нет сети — наверх, как раньше у whoami. Остальное — старый backend без /start
// (404, откат), сбой сервера, неожиданный ответ — прежний путь по одному запросу: там
// сбой списка чатов не роняет старт, а показывается в списке (К4).
let chatOpenSupported = false;

// Ручка открытия чата одним запросом есть там же, где /start: экран чата спрашивает здесь.
export function supportsChatOpen() {
  return chatOpenSupported;
}

async function startInOneRequest() {
  try {
    const start = await api.start();
    userStore.set(start.userId);
    applyAppSettingsResult(start.settings);
    applyModelsResult(start.models);
    applyChatsResult(start.chats, false);
    chatsFromStart = true;
  } catch (error) {
    if (error?.status === 401 || error?.status === 0) {
      throw error;
    }
    console.warn('initApp: /start unavailable, loading one by one', error);
    return false;
  }
  chatOpenSupported = true;
  return true;
}

async function runAfterStart(tasks) {
  for (const task of tasks) {
    try {
      await task();
    } catch (error) {
      console.warn('initApp: background load failed (continuing)', error);
    }
  }
}

// Отложенный старт MQTT: вызывается ПОСЛЕ первого рендера (из App.svelte), чтобы
// тяжёлый mqtt-чанк не блокировал монтаж. Ошибки терпимы — статусы тянутся из /api.
export async function startMqtt() {
  const currentUser = get(userStore);
  if (!currentUser) {
    return;
  }
  try {
    const mqtt = await loadMqtt();
    await mqtt.connect(currentUser);
  } catch (error) {
    console.warn('MQTT start failed (continuing)', error);
  }
}

// Отметка последнего ПОЛНОГО resume — для гейта против лишнего трафика при частых
// коротких app-switch (см. resumeApp). MQTT/активность/reconcile идут всегда, а список
// чатов и настройки обновляются не чаще, чем раз в RESUME_GATE_MS.
let lastFullResumeAt = 0;
const RESUME_GATE_MS = 30000;

export async function resumeApp() {
  if (resumePromise) {
    return resumePromise;
  }

  resumePromise = (async () => {
    const currentUser = get(userStore);
    if (!currentUser) {
      await initApp();
      return;
    }

    const now = Date.now();
    const fresh = now - lastFullResumeAt < RESUME_GATE_MS;
    if (!fresh) {
      lastFullResumeAt = now;
    }

    // Всегда: реконнект MQTT + восстановление активных генераций + (ниже) reconcile
    // сообщений открытого чата — лёгкие запросы, нужные для консистентности. По гейту
    // (не чаще раза в RESUME_GATE_MS): тяжёлые обновления списка чатов и настроек.
    const [mqttResult, , , settingsResult] = await Promise.allSettled([
      loadMqtt().then((m) => m.ensureConnected(currentUser)),
      fresh ? Promise.resolve() : refreshChatsInBackground(),
      syncActivityFromApi(),
      fresh ? Promise.resolve() : loadAppSettings(),
    ]);

    if (mqttResult.status === 'rejected') {
      console.warn('MQTT reconnect failed during resume', mqttResult.reason);
    }

    if (settingsResult.status === 'rejected') {
      throw settingsResult.reason;
    }

    // Дозагрузить сообщения открытого чата: финальные ответы, пришедшие пока PWA был
    // в фоне (MQTT мёртв, но backend сохранил их в SQLite). Chat.svelte слушает событие.
    dispatchAppResumed();

    // Промоушен офлайн-очереди: сеть вернулась (resume после online/visible). Если
    // WSS пережил офлайн, реконнект-хука не будет — flush отсюда единственный путь.
    flushPendingDelivery();

    // Этап 28: подписку iOS могла удалить, пока приложение было открыто или в фоне.
    if (!fresh) {
      void keepPushSubscriptionSafe({ resave: false });
    }
  })().finally(() => {
    resumePromise = null;
  });

  return resumePromise;
}

// Единая точка «дозагрузить открытый чат» (Chat.svelte слушает app:resumed →
// reconcileLatestMessages). Метка времени — для дебаунса хука реконнекта.
let lastAppResumedAt = 0;

function dispatchAppResumed() {
  lastAppResumedAt = Date.now();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('app:resumed'));
  }
}

// Хук успешного MQTT-РЕконнекта (вызывает mqtt.js из обработчика 'connect').
// clean:true — за время разрыва брокер выбросил всё неретейнутое (включая финальный
// response): дотягиваем состояние из /api. Дебаунс 2с — resumeApp сам шлёт
// app:resumed, не дублируем reconcile при resume-переподключении.
export function handleMqttReconnected() {
  syncActivityFromApi();
  // Недоставленные запросы — немедленный ре-publish (не ждать их backoff-таймеры).
  flushPendingDelivery();
  if (Date.now() - lastAppResumedAt > 2000) {
    dispatchAppResumed();
  }
}

// ===========================================
// Вотчдог видимой сессии: iOS может потерять событие 'online' (navigator.onLine
// остаётся true при фактически ожившей сети) — тогда backoff-реконнект дойдёт до
// капа 120с, а форсировать его некому. Раз в 30с: если вкладка видима, браузер
// считает себя online, а MQTT не подключён непрерывно ≥60с — лёгкий пинг backend;
// сеть жива → ensureConnected (полный connect, немедленная попытка). Гейт 60с
// между форсами — контролируемая частота, шторма нет.
// ===========================================

const WATCHDOG_INTERVAL_MS = 30000;
const WATCHDOG_DISCONNECT_THRESHOLD_MS = 60000;
const WATCHDOG_FORCE_GAP_MS = 60000;
const WATCHDOG_PING_TIMEOUT_MS = 4000;

let watchdogTimer = null;
let lastConnectedAt = Date.now();
let lastWatchdogForceAt = 0;
let watchdogUnsubscribe = null;

async function pingBackendAlive() {
  if (typeof fetch === 'undefined') {
    return false;
  }
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? globalThis.setTimeout(() => controller.abort(), WATCHDOG_PING_TIMEOUT_MS) : null;
  try {
    // Любой HTTP-ответ (даже 401) = сеть до backend жива.
    await fetch('/api/whoami', { credentials: 'same-origin', cache: 'no-store', signal: controller?.signal });
    return true;
  } catch (_) {
    return false;
  } finally {
    if (timer) {
      globalThis.clearTimeout(timer);
    }
  }
}

export function startConnectivityWatchdog() {
  if (watchdogTimer || typeof window === 'undefined') {
    return;
  }
  watchdogUnsubscribe = connectionStore.subscribe((state) => {
    if (state === 'connected') {
      lastConnectedAt = Date.now();
    }
  });
  watchdogTimer = window.setInterval(async () => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return;
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return;
    }
    const currentUser = get(userStore);
    if (!currentUser || get(connectionStore) === 'connected') {
      return;
    }
    const now = Date.now();
    if (now - lastConnectedAt < WATCHDOG_DISCONNECT_THRESHOLD_MS) {
      return;
    }
    if (now - lastWatchdogForceAt < WATCHDOG_FORCE_GAP_MS) {
      return;
    }
    lastWatchdogForceAt = now;
    if (!(await pingBackendAlive())) {
      return;
    }
    console.log('Вотчдог: сеть жива, MQTT нет — форсируем переподключение');
    loadMqtt()
      .then((m) => m.ensureConnected(currentUser))
      .catch((error) => console.warn('Вотчдог: переподключение не удалось', error));
  }, WATCHDOG_INTERVAL_MS);
}

// В8 (1.27.2): тап по плашке «Нет связи» — немедленная попытка, не ждать backoff.
export function reconnectNow() {
  const currentUser = get(userStore);
  if (!currentUser) {
    return Promise.resolve();
  }
  return loadMqtt()
    .then((m) => m.ensureConnected(currentUser))
    .catch((error) => {
      console.warn('manual reconnect failed', error);
    });
}

export function stopConnectivityWatchdog() {
  if (watchdogTimer && typeof window !== 'undefined') {
    window.clearInterval(watchdogTimer);
    watchdogTimer = null;
  }
  if (watchdogUnsubscribe) {
    watchdogUnsubscribe();
    watchdogUnsubscribe = null;
  }
}

export async function loadChats(archived = false, { silent = false } = {}) {
  // silent=true — тихое фоновое обновление на resume: НЕ дёргаем loadingStore,
  // чтобы список не мигал спиннером (chatsStore обновится, {#each} переиспользует DOM).
  if (!silent) {
    loadingStore.set(true);
  }
  try {
    let result;
    try {
      result = await api.listChats(archived);
    } catch (error) {
      chatsLoadErrorStore.set(TEXT.chats.loadFailed);
      throw error;
    }
    applyChatsResult(result, archived);
    return result.items;
  } finally {
    if (!silent) {
      loadingStore.set(false);
    }
  }
}

// Ответ списка чатов — из GET /chats или из /start (этап 30).
let chatsLoadedAt = 0;

function applyChatsResult(result, archived) {
  chatsLoadErrorStore.set('');
  chatsLoadedStore.set(true);
  chatsStore.set(normalizeChats(result.items));
  archivedCountStore.set(Number(result.archivedCount) || 0);
  applyBrainStatus(result.brain);
  // Цифра на иконке (этап 9): считает backend, клиент только ставит.
  applyAppBadge(result.unreadTotal);
  showArchivedStore.set(archived);
  chatsLoadedAt = Date.now();
}

// Этап 30: экран списка, появившийся сразу после старта, не перечитывает список,
// который принёс /start (второй запрос шёл вслед за первым). Разово и только пока
// ответ свежий: возврат из чата по-прежнему тихо обновляет список (В4).
export const CHATS_FRESH_MS = 5000;
let chatsFromStart = false;

export function takeFreshStartChats(now = Date.now()) {
  const fresh = chatsFromStart && now - chatsLoadedAt < CHATS_FRESH_MS;
  chatsFromStart = false;
  return fresh;
}

// Тихое перечитывание списка чатов — склеенное (1.24.2). Ответ Петровича приходил
// и как response, и как событие message, каждый обработчик дёргал список (94 КБ на
// вызов) — в логе по два-три /chats за секунду. Теперь вызовы за короткое окно
// сливаются в один запрос, а пришедшие во время полёта — в один следующий.
export const CHATS_REFRESH_DEBOUNCE_MS = 150;
let chatsRefreshPending = null;
let chatsRefreshInFlight = null;

export function refreshChatsInBackground() {
  if (!chatsRefreshPending) {
    let resolve;
    const promise = new Promise((done) => { resolve = done; });
    chatsRefreshPending = { promise, resolve };
    globalThis.setTimeout(runChatsRefresh, CHATS_REFRESH_DEBOUNCE_MS);
  }
  return chatsRefreshPending.promise;
}

async function runChatsRefresh() {
  const pending = chatsRefreshPending;
  chatsRefreshPending = null;
  if (chatsRefreshInFlight) {
    await chatsRefreshInFlight;
  }
  chatsRefreshInFlight = (async () => {
    try {
      await loadChats(get(showArchivedStore), { silent: true });
    } catch (error) {
      console.error('Failed to refresh chats', error);
    }
  })();
  await chatsRefreshInFlight;
  chatsRefreshInFlight = null;
  pending.resolve();
}

// Источник истины при возврате из фона: восстанавливаем состояние генераций
// через /api (надёжно), не полагаясь на MQTT realtime, который рвётся в фоне.
export async function syncActivityFromApi() {
  try {
    const { items = [] } = await api.getActivity();
    // Запрос в /api/activity = дошёл до бэкенда (ACK доставки без MQTT).
    for (const item of items) {
      if (item.requestId) {
        acknowledgeDelivery(item.requestId);
      }
    }
    const activeChatIds = new Set(items.map((item) => item.chatId));
    chatActivityStore.update((current) => {
      let next = current;
      // Активные генерации (queued/processing) из backend.
      for (const item of items) {
        next = applyStatusToActivities(next, {
          chatId: item.chatId,
          requestId: item.requestId,
          model: item.model,
          state: item.state === 'processing' ? 'processing' : 'queued',
        });
      }
      // Снять «зависшую» активность: была генерация, а backend её не показывает →
      // завершилась, пока PWA был в фоне (ответ уже в сообщениях).
      for (const [chatId, activity] of Object.entries(next)) {
        if (!activeChatIds.has(chatId) && isGeneratingState(activity?.state) && activity?.requestId) {
          next = applyStatusToActivities(next, {
            chatId,
            requestId: activity.requestId,
            state: 'idle',
          });
        }
      }
      return next;
    });
  } catch (error) {
    console.warn('Failed to sync activity from API', error);
  }
}

export async function loadModels() {
  const result = await api.listModels();
  applyModelsResult(result);
  return result.items;
}

// Этап 33.2: после смены каталога — модели, общие настройки («модель по умолчанию»
// могла сброситься) и уже загруженные шаблоны. Сбой не страшен: всё придёт заново
// с /api/start при следующем запуске.
async function reloadAfterCatalogChange() {
  const jobs = [loadModels(), loadAppSettings()];
  if (templatesLoaded) {
    jobs.push(loadTemplates());
  }
  const results = await Promise.allSettled(jobs);
  for (const result of results) {
    if (result.status === 'rejected') {
      console.warn('Failed to reload after models_changed', result.reason);
    }
  }
}

function applyModelsResult(result) {
  modelsStore.set(result.items);
  serviceModelsStore.set(result.serviceModels ?? null);
}

// Локальная отметка «прочитано» для текущего пользователя (этап 7.3): сбросить
// счётчик и продвинуть свою метку в составе — не дожидаясь перезагрузки списка.
export function markChatReadLocally(chatId, userId, lastReadTs, unreadTotal = null) {
  chatsStore.update((current) => current.map((chat) => {
    if (chat.chatId !== chatId) {
      return chat;
    }
    const members = Array.isArray(chat.members)
      ? chat.members.map((member) => (
          member.userId === userId && (!member.lastReadTs || member.lastReadTs < lastReadTs)
            ? { ...member, lastReadTs }
            : member
        ))
      : chat.members;
    return { ...chat, unreadCount: 0, lastReadTs, members };
  }));
  // Прочитали чат — иконка обновляется сразу цифрой из ответа сервера.
  if (unreadTotal !== null && unreadTotal !== undefined) {
    applyAppBadge(unreadTotal);
  }
}

// Этап 28: плашка «Уведомления на этом устройстве выключены» в списке чатов — iOS
// сбросила разрешение, и вернуть подписку можно только касанием.
export const pushRestoreStore = writable(false);

let pushKeepPromise = null;

// Этап 28: push-подписка устройства держится сама. На старте пересохраняется на backend
// (после перелогина или ротации endpoint) и сверяется с ключом сервера, при возврате в
// приложение — проверяется; пропавшая пересоздаётся без касаний, а если сброшено само
// разрешение — показывается плашка. Одна проверка за раз: без service worker
// serviceWorker.ready не разрешается никогда. Ошибки — не критичны.
function keepPushSubscriptionSafe({ resave }) {
  if (pushKeepPromise) {
    return pushKeepPromise;
  }
  pushKeepPromise = (async () => {
    const push = await import('./push.js');
    if (!push.isPushSupported()) {
      return;
    }
    const hasNotifiedChats = get(chatsStore).some((chat) => chat.notificationsEnabled);
    const result = await push.keepPushSubscription({ wanted: push.isPushWanted(hasNotifiedChats), resave });
    pushRestoreStore.set(result === 'needs-tap');
  })()
    .catch((error) => {
      console.warn('push subscription check failed', error);
    })
    .finally(() => {
      pushKeepPromise = null;
    });
  return pushKeepPromise;
}

// Плашка, «Включить»: одно касание до рабочей подписки. Разрешение спрашиваем первым
// действием в тапе (iOS показывает запрос только внутри жеста), ключ сервера берём
// запомненный при проверке. Итог: 'granted' | 'denied' | 'no-push'.
export async function restorePushFromBanner() {
  let permission = typeof Notification === 'undefined' ? 'denied' : Notification.permission;
  if (permission === 'default') {
    try {
      permission = await Notification.requestPermission();
    } catch (error) {
      permission = Notification.permission;
    }
  }
  const push = await import('./push.js');
  let result = 'denied';
  if (permission === 'granted') {
    try {
      const publicKey = push.getKnownPublicKey() || (await api.getPushConfig())?.publicKey;
      result = publicKey && (await push.subscribeToPush(publicKey)) === 'granted' ? 'granted' : 'no-push';
    } catch (error) {
      console.warn('push banner: subscription failed', error);
      result = 'no-push';
    }
  }
  // Запрет не лечится касанием — плашку убираем; сбой подписки — оставляем для повтора.
  if (result !== 'no-push') {
    pushRestoreStore.set(false);
  }
  return result;
}

// Плашка, «Не нужно»: выбор этого устройства, больше не предлагаем и не восстанавливаем.
export async function dismissPushBanner() {
  const push = await import('./push.js');
  push.setPushIntent('off');
  pushRestoreStore.set(false);
}

export function pushRestoreResultText(result) {
  return {
    granted: TEXT.push.restored,
    denied: TEXT.push.restoreDenied,
  }[result] || TEXT.push.failed;
}

// Уведомления по чату (этап 12): выключены по умолчанию, включаются точечно.
// Включённые дают push, звук и уведомление в браузере; выключенные — полную тишину.
export function isChatNotificationsOn(chatId) {
  return Boolean(get(chatsStore).find((chat) => chat.chatId === chatId)?.notificationsEnabled);
}

export async function toggleChatNotifications(chatId, enabled) {
  await api.setChatNotifications(chatId, enabled);
  chatsStore.update((current) => current.map((chat) => (
    chat.chatId === chatId ? { ...chat, notificationsEnabled: Boolean(enabled) } : chat
  )));
  publishSyncEvent('chats:refresh');
}

// В12 (1.27.6): «Включить уведомления» в чате — одним тапом до рабочего результата.
// Раньше флаг чата включался, а push-подписку устройства и общий флажок уведомлений
// в приложении надо было искать в общих настройках. Разрешение браузера спрашиваем
// первым же действием: iOS показывает запрос только внутри тапа.
// Итог: 'granted' | 'denied' | 'need-install' | 'no-push'.
export async function enableChatNotifications(chatId) {
  let permission = typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
  if (permission === 'default') {
    try {
      permission = await Notification.requestPermission();
    } catch (error) {
      permission = Notification.permission;
    }
  }
  await toggleChatNotifications(chatId, true);

  const push = await import('./push.js');
  let result = 'granted';
  if (!push.isPushSupported()) {
    result = push.isIos() && !push.isStandalone() ? 'need-install' : 'no-push';
  } else if (permission !== 'granted') {
    result = 'denied';
  } else {
    try {
      if (!(await push.getCurrentSubscription())) {
        const config = await api.getPushConfig();
        const state = config?.enabled && config.publicKey ? await push.subscribeToPush(config.publicKey) : 'no-push';
        if (state !== 'granted') {
          result = 'no-push';
        }
      }
    } catch (error) {
      console.warn('chat notifications: push subscription failed', error);
      result = 'no-push';
    }
  }
  if (result === 'granted') {
    pushRestoreStore.set(false);
  }
  if (permission === 'granted' && !get(appSettingsStore).browserNotificationsEnabled) {
    await saveAppSettings({ ...get(appSettingsStore), browserNotificationsEnabled: true })
      .catch((error) => console.warn('chat notifications: app setting not saved', error));
  }
  return result;
}

export function chatNotificationsResultText(result) {
  return {
    granted: TEXT.family.notificationsOn,
    denied: TEXT.family.notificationsDenied,
    'need-install': TEXT.family.notificationsNeedInstall,
  }[result] || TEXT.family.notificationsNoPush;
}

// Закрепление чата сверху (этап 11): личный флаг участника, лимит проверяет сервер
// (409 при четвёртом — ошибка уходит наверх, её разбирает экран списка).
export async function toggleChatPin(chatId, pinned) {
  const result = await api.pinChat(chatId, pinned);
  const pinnedAt = pinned ? (result?.pinnedAt || new Date().toISOString()) : null;
  chatsStore.update((items) => normalizeChats(items.map((item) => (
    item.chatId === chatId ? { ...item, isPinned: Boolean(pinnedAt), pinnedAt } : item
  ))));
  publishSyncEvent('chats:refresh');
}

// Мозг чата (этап 10): 'cloud' — облачный Петрович, 'dacha' — демон на даче.
// Переключает владелец на экране контекста; ошибка (403/409) уходит наверх как есть.
export async function setChatBrain(chatId, brain) {
  const next = brain === 'dacha' ? 'dacha' : 'cloud';
  await api.updateChatSettings(chatId, { brain: next });
  chatsStore.update((current) => current.map((chat) => (
    chat.chatId === chatId ? { ...chat, brain: next } : chat
  )));
  publishSyncEvent('chats:refresh');
}

export async function loadUsers() {
  const result = await api.listUsers();
  const map = {};
  for (const item of result.items || []) {
    map[item.userId] = item;
  }
  usersStore.set(map);
  return map;
}

const LEGACY_AVATAR_MAX_BYTES = 60000;

// Унаследованные аватары сохранялись без сжатия (сотни КБ) и тянулись в каждом
// settings/app. При первой загрузке ужимаем их до 256px и пересохраняем —
// fire-and-forget, чтобы не задерживать старт.
async function maybeShrinkLegacyAvatar(settings) {
  const avatar = settings.avatarDataUrl;
  if (!avatar || avatar.length < LEGACY_AVATAR_MAX_BYTES) {
    return;
  }
  try {
    const shrunk = await compressImageDataUrl(avatar, { maxDimension: 256, quality: 0.85 });
    if (shrunk && shrunk.length < avatar.length) {
      await saveAppSettings({ ...settings, avatarDataUrl: shrunk });
    }
  } catch (error) {
    console.warn('Failed to shrink legacy avatar', error);
  }
}

export async function loadAppSettings() {
  return applyAppSettingsResult(await api.getAppSettings());
}

function applyAppSettingsResult(raw) {
  const result = normalizeAppSettings(raw);
  appSettingsStore.set(result);
  applyAppSettingsToDocument(result);
  setCachedAppSettings(result);
  maybeShrinkLegacyAvatar(result);
  return result;
}

export async function saveAppSettings(nextSettings) {
  const payload = normalizeAppSettings(nextSettings);
  const saved = normalizeAppSettings(await api.updateAppSettings(payload));
  appSettingsStore.set(saved);
  applyAppSettingsToDocument(saved);
  setCachedAppSettings(saved);
  return saved;
}

// Заготовка строки только что созданного чата (В3, 1.27.3): переход в него не ждёт
// перечитывания списка — сервер ответил chatId, остальное подтянет фоновое обновление.
export function newChatListItem(chatId, { title = null, chatType = null, templateId = null, now = new Date() } = {}) {
  const ts = now.toISOString();
  return {
    chatId,
    title: title || null,
    isArchived: false,
    createdAt: ts,
    updatedAt: ts,
    chatType: templateId ? 'default' : (chatType || 'default'),
    lastMessagePreview: null,
    lastMessageRole: null,
    lastMessageTs: null,
    hasImages: false,
    isOwner: true,
    isShared: false,
    members: [],
    responseMode: 'all',
    brain: 'cloud',
    unreadCount: 0,
    notificationsEnabled: false,
    isPinned: false,
    pinnedAt: null,
    templateId: templateId || null,
  };
}

export async function createChat(title = null, chatType = null, templateId = null) {
  const result = await api.createChat(title, chatType, templateId);
  // В3 (1.27.3): раньше до перехода ждали перечитывания списка (а экран списка — ещё
  // одного): три запроса подряд на тап «Новый чат». Теперь строка — сразу, список — фоном.
  chatsStore.update((items) => (items.some((item) => item.chatId === result.chatId)
    ? items
    : normalizeChats([newChatListItem(result.chatId, { title, chatType, templateId }), ...items])));
  setCurrentChat(result.chatId);
  void refreshChatsInBackground();
  publishSyncEvent('chats:refresh');
  if (templateId) {
    // Шаблон поднялся в списке по last_used_at — обновляем библиотеку тихо.
    loadTemplates().catch(() => {});
  }
  return result.chatId;
}

// Библиотека шаблонов чата (этап 24): личный список живёт в store, чтобы шторка «+»
// открывалась мгновенно; подтягивается вместе со списком чатов и на экране библиотеки.
export const templatesStore = writable([]);

let templatesLoaded = false;

export async function loadTemplates() {
  try {
    const result = await api.listTemplates();
    templatesStore.set(Array.isArray(result?.items) ? result.items : []);
    templatesLoaded = true;
  } catch (error) {
    // Прежний backend ручки не знает — библиотека просто пуста.
    console.warn('Failed to load templates', error);
  }
  return get(templatesStore);
}

// Этап 30: экрану списка библиотека нужна для шторки «+» — достаточно раз за сеанс
// (раньше запрос уходил при каждом возврате из чата). Правки библиотеки идут через
// loadTemplates и обновляют её сразу.
export function ensureTemplatesLoaded() {
  return templatesLoaded ? Promise.resolve(get(templatesStore)) : loadTemplates();
}

export async function deleteChat(chatId) {
  await api.deleteChat(chatId);
  chatsStore.update((items) => items.filter((item) => item.chatId !== chatId));
  clearChatRuntime(chatId);
  clearMessageSnapshot(chatId);
  if (get(currentChatId) === chatId) {
    currentChatId.set(null);
  }
  publishSyncEvent('chat:deleted', { chatId });
  publishSyncEvent('chats:refresh');
}

export async function toggleArchive(chatId, archive) {
  if (archive) {
    await api.archiveChat(chatId);
  } else {
    await api.unarchiveChat(chatId);
  }
  await refreshChatsInBackground();
  publishSyncEvent('chats:refresh');
}

export async function renameChat(chatId, title) {
  await api.updateChatTitle(chatId, title);
  chatsStore.update((items) => normalizeChats(items.map((item) => (
    item.chatId === chatId ? { ...item, title } : item
  ))));
  publishSyncEvent('chats:refresh');
}

export function setCurrentChat(chatId) {
  currentChatId.set(chatId);
}

export function classifyChatError(error, fallback = 'Не удалось выполнить запрос.') {
  if (!(error instanceof ApiError)) {
    return fallback;
  }

  if (error.code === 'NETWORK') {
    return TEXT.api.network;
  }
  if (error.code === 'TIMEOUT') {
    return TEXT.api.timeout;
  }
  if (error.status === 401) {
    return TEXT.api.sessionExpired;
  }
  if (error.status === 403) {
    return TEXT.api.forbidden;
  }
  if (error.status === 404) {
    return TEXT.api.chatMissing;
  }
  if (error.status >= 500) {
    return TEXT.api.serverBusy;
  }
  return error.message || fallback;
}

export function setDraft(chatId, value, { broadcast = true } = {}) {
  draftStore.update((current) => ({ ...current, [chatId]: value }));
  setDraftStorage(chatId, value);
  if (broadcast) {
    publishSyncEvent('draft:changed', { chatId });
  }
}

export function getDraft(chatId) {
  const storeValue = get(draftStore)[chatId];
  if (typeof storeValue === 'string') {
    return storeValue;
  }
  const stored = getDraftStorage(chatId);
  if (stored) {
    draftStore.update((current) => ({ ...current, [chatId]: stored }));
  }
  return stored;
}

export function clearDraft(chatId, { broadcast = true } = {}) {
  draftStore.update((current) => {
    const next = { ...current };
    delete next[chatId];
    return next;
  });
  clearDraftStorage(chatId);
  if (broadcast) {
    publishSyncEvent('draft:changed', { chatId });
  }
}

export function setShowSystemPreference(chatId, value, { broadcast = true } = {}) {
  uiPrefsStore.update((current) => ({
    ...current,
    [chatId]: {
      ...(current[chatId] || {}),
      showSystemMessages: value,
    },
  }));
  setShowSystemMessagesStorage(chatId, value);
  if (broadcast) {
    publishSyncEvent('pref:show-system', { chatId });
  }
}

export function getShowSystemPreference(chatId) {
  const current = get(uiPrefsStore)[chatId]?.showSystemMessages;
  if (typeof current === 'boolean') {
    return current;
  }
  const stored = getShowSystemMessages(chatId);
  uiPrefsStore.update((existing) => ({
    ...existing,
    [chatId]: {
      ...(existing[chatId] || {}),
      showSystemMessages: stored,
    },
  }));
  return stored;
}

export function clearChatRuntime(chatId) {
  clearDraft(chatId, { broadcast: false });
  uiPrefsStore.update((current) => {
    const next = { ...current };
    delete next[chatId];
    return next;
  });
  chatActivityStore.update((current) => {
    const next = { ...current };
    delete next[chatId];
    return next;
  });
}

function isDocumentHidden() {
  return typeof document !== 'undefined' && document.visibilityState !== 'visible';
}

async function ensureAudioContext() {
  if (typeof window === 'undefined') {
    return null;
  }
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) {
    return null;
  }
  if (!audioContext) {
    audioContext = new Ctor();
  }
  if (audioContext.state === 'suspended') {
    await audioContext.resume();
  }
  return audioContext;
}

async function playTone(frequency, durationMs) {
  const ctx = await ensureAudioContext();
  if (!ctx) {
    return;
  }
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();
  oscillator.type = 'sine';
  oscillator.frequency.value = frequency;
  gainNode.gain.setValueAtTime(0.0001, ctx.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.05, ctx.currentTime + 0.01);
  gainNode.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durationMs / 1000);
  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);
  oscillator.start();
  oscillator.stop(ctx.currentTime + durationMs / 1000);
}

async function playUiSound(kind) {
  const settings = get(appSettingsStore);
  if (!settings.soundEnabled) {
    return;
  }
  if (kind === 'send' && !settings.sendSoundEnabled) {
    return;
  }
  if (kind === 'response' && !settings.responseSoundEnabled) {
    return;
  }
  try {
    await playTone(kind === 'send' ? 660 : 520, kind === 'send' ? 90 : 160);
  } catch (error) {
    console.warn('Failed to play UI sound', error);
  }
}

function maybeShowBrowserNotification(payload) {
  const settings = get(appSettingsStore);
  if (!settings.browserNotificationsEnabled || !isDocumentHidden()) {
    return;
  }
  if (typeof window === 'undefined' || !('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }

  const chat = get(chatsStore).find((item) => item.chatId === payload.chatId);
  const title = chat?.title || 'Новый ответ';
  const body = (payload.text || '').slice(0, 140) || 'Ассистент прислал новый ответ.';
  const notification = new Notification(title, {
    body,
    tag: `chat-response-${payload.chatId}`,
    renotify: true,
  });
  notification.onclick = () => {
    window.focus();
    window.location.hash = `/chat/${payload.chatId}`;
    notification.close();
  };
}

export function applyStatusPayload(payload) {
  // Любой статус по requestId = бэкенд запрос видел → доставка подтверждена.
  if (payload?.requestId) {
    acknowledgeDelivery(payload.requestId);
  }
  chatActivityStore.update((current) => applyStatusToActivities(current, payload));
  window.dispatchEvent(new CustomEvent('mqtt:status', { detail: payload }));
}

export function applyPartialPayload(payload) {
  chatActivityStore.update((current) => applyPartialToActivities(current, payload));
}

export function applyResponsePayload(payload) {
  if (payload?.requestId) {
    acknowledgeDelivery(payload.requestId);
  }
  chatActivityStore.update((current) => applyResponseToActivities(current, payload));
  refreshChatsInBackground();
  publishSyncEvent('chats:refresh');
  // Звук ответа — только когда пользователь на него НЕ смотрит (вкладка скрыта или
  // открыт другой чат): тот же гейт, что у браузерных уведомлений. Чат без
  // включённых уведомлений (этап 12) — молча.
  if (isChatNotificationsOn(payload?.chatId)) {
    if (isDocumentHidden() || get(currentChatId) !== payload?.chatId) {
      playUiSound('response');
    }
    maybeShowBrowserNotification(payload);
  }
  window.dispatchEvent(new CustomEvent('mqtt:response', { detail: payload }));
}

export function applyTechPayload(payload) {
  chatActivityStore.update((current) => applyTechToActivities(current, payload));
  window.dispatchEvent(new CustomEvent('mqtt:tech', { detail: payload }));
}

// События семейных чатов (/event): 'message' — сообщение другого участника,
// 'members' — изменение состава (shared|removed|left|chat_deleted),
// 'list' — снимок общего списка чата (этап 8).
export function applyEventPayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return;
  }
  if (payload.type === 'brain_status') {
    // Состояние «мозга» (этап 25.1) — не про чат, поэтому без chatId.
    applyBrainStatus(payload);
    window.dispatchEvent(new CustomEvent('mqtt:event', { detail: payload }));
    return;
  }
  if (payload.type === 'models_changed') {
    // Каталог выбора сменился (этап 33.2): модель включили, выключили, сделали
    // основной или она пропала у провайдера — чаты на ней уже переведены на основную.
    // Шторка выбора, общие настройки и шаблоны перечитываются, шапку открытого чата
    // обновляет Chat.svelte по тому же событию.
    reloadAfterCatalogChange();
    window.dispatchEvent(new CustomEvent('mqtt:event', { detail: payload }));
    return;
  }
  if (!payload.chatId) {
    return;
  }
  if (payload.type === 'read') {
    // Метка прочтения другого участника — патчим состав чата локально.
    chatsStore.update((current) => applyReadMarkToChats(current, payload));
  } else if (payload.type === 'typing') {
    // Эфемерно: только событие для открытого чата, список не трогаем.
  } else if (payload.type === 'message') {
    refreshChatsInBackground();
    publishSyncEvent('chats:refresh');
    // Тот же гейт, что у ответа Петровича: звук только когда чат не на экране;
    // чат без включённых уведомлений — молча.
    if (isChatNotificationsOn(payload.chatId)) {
      if (isDocumentHidden() || get(currentChatId) !== payload.chatId) {
        playUiSound('response');
      }
      maybeShowBrowserNotification({ chatId: payload.chatId, text: payload.message?.text });
    }
  } else if (payload.type === 'members') {
    refreshChatsInBackground();
    publishSyncEvent('chats:refresh');
  } else if (payload.type === 'message_deleted') {
    // Удаление у другого участника или на другом своём устройстве (этап 15): ленту
    // правит открытый чат, а список обновляем — превью и непрочитанные считает сервер.
    // Без звука и уведомлений: это не новое сообщение.
    refreshChatsInBackground();
    publishSyncEvent('chats:refresh');
  } else if (payload.type === 'list') {
    // Правка общего списка (этап 8): снимок разбирает открытая панель, а список
    // чатов обновляем ради сводки «3 из 7». Без звука и уведомлений — это не сообщение.
    refreshChatsInBackground();
    publishSyncEvent('chats:refresh');
  } else if (payload.type === 'stage') {
    // Этапы проекта (этап 23): карточку и сводку разбирают открытый чат и экран
    // контекста. Конспект — не новое сообщение: без звука, уведомлений и сдвига чата.
  } else if (payload.type === 'publication') {
    // Публикация на сайт (этап 26.4), только владельцу: значок и «правку из git»
    // разбирают открытый чат и экран контекста. Без звука и уведомлений.
  } else {
    return;
  }
  window.dispatchEvent(new CustomEvent('mqtt:event', { detail: payload }));
}

export function generateRequestId() {
  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

export function playSendSound() {
  return playUiSound('send');
}

export function teardownApp() {
  clearDelivery();
  clearMqttError();
  loadMqtt().then((m) => m.disconnect()).catch(() => {});
  if (syncCleanup) {
    syncCleanup();
    syncCleanup = null;
  }
}
